import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { TwService } from '../modules/tailwind/services'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  foobar?: string

  // METHODS
  sayHello: SbTemplateComponent['sayHello']

  // TEMPLATES
}

/**
 * A simple template for new component, just make a copy of this template, adjust the class / tag name and add a new export to `src/riba/components/index.ts`.
 */
export class SbTemplateComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-template foobar="hello">...</sb-template>`
   */
  public static tagName = 'sb-template'

  protected tailwind = TwService.getSingleton()

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // OPTIONS
    foobar: undefined,

    // METHODS
    sayHello: this.sayHello.bind(this),

    // TEMPLATES
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return ['foobar']
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbTemplateComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbTemplateComponent.tagName}-ready`)
  }

  protected onBreakpointChanged(/*bp: Breakpoint*/) {
    // console.debug("onBreakpointChanged", bp);
  }

  protected addEventListeners() {
    this.tailwind.on('breakpoint:changed', this.onBreakpointChanged)
  }

  protected removeEventListeners() {
    this.tailwind.off('breakpoint:changed', this.onBreakpointChanged)
  }

  public sayHello() {
    console.log('Hello')
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has not it's own template, you need to add the child
          HTML in the template
        </p>
      )
    }
    return null
  }
}
